import { useEffect, useRef } from 'react';
import {
  trackCheckoutView,
  trackFormFieldCompletion,
  trackPaymentMethodChange,
  trackBumpInteraction,
  trackCheckoutButtonClick
} from '../../utils/checkoutTracking';

/**
 * Componente invisível que monitora eventos do checkout
 * Este componente não renderiza nada visualmente, apenas observa eventos
 */
interface CheckoutAnalyticsProps {
  checkoutRef?: React.RefObject<HTMLDivElement>;
  offerId?: string;
}

const CheckoutAnalytics: React.FC<CheckoutAnalyticsProps> = ({
  checkoutRef,
  offerId
}) => {
  const isInitialPageViewTracked = useRef(false);
  const formElementsMonitored = useRef<string[]>([]);
  
  // Rastrear visualização da página
  useEffect(() => {
    if (!isInitialPageViewTracked.current) {
      trackCheckoutView(offerId);
      isInitialPageViewTracked.current = true;
    }
  }, [offerId]);

  // Monitorar cliques no botão de finalizar compra
  useEffect(() => {
    if (!checkoutRef?.current) return;
    
    const submitButton = checkoutRef.current.querySelector('button[type="submit"]');
    const formElement = checkoutRef.current.querySelector('form');
    
    if (submitButton) {
      const handleButtonClick = () => {
        // Capturar o valor total do pedido se estiver disponível no DOM
        const totalAmountElement = checkoutRef.current?.querySelector('[data-total-amount]');
        let totalAmount = undefined;
        
        if (totalAmountElement && totalAmountElement.getAttribute('data-total-amount')) {
          totalAmount = parseFloat(totalAmountElement.getAttribute('data-total-amount') || '0');
        }
        
        trackCheckoutButtonClick(offerId, totalAmount);
      };
      
      submitButton.addEventListener('click', handleButtonClick);
      
      return () => {
        submitButton.removeEventListener('click', handleButtonClick);
      };
    }
    
    // Monitorar campos do formulário (apenas uma vez)
    if (formElement && formElementsMonitored.current.length === 0) {
      const inputFields = formElement.querySelectorAll('input, select');
      
      inputFields.forEach(field => {
        const fieldName = field.getAttribute('name') || field.getAttribute('id') || 'unknown';
        
        if (!formElementsMonitored.current.includes(fieldName)) {
          formElementsMonitored.current.push(fieldName);
          
          const handleBlur = (e: Event) => {
            const target = e.target as HTMLInputElement | HTMLSelectElement;
            const isValid = target.checkValidity();
            trackFormFieldCompletion(fieldName, isValid, offerId);
          };
          
          field.addEventListener('blur', handleBlur);
        }
      });
    }
    
    // Monitorar seleção de método de pagamento
    const paymentMethods = checkoutRef.current.querySelectorAll('[name="paymentMethod"]');
    paymentMethods.forEach(method => {
      const handlePaymentChange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (target.checked) {
          trackPaymentMethodChange(target.value, offerId);
        }
      };
      
      method.addEventListener('change', handlePaymentChange);
    });
    
    // Monitorar interações com os order bumps
    const orderBumps = checkoutRef.current.querySelectorAll('[data-bump-id]');
    orderBumps.forEach(bump => {
      const bumpId = bump.getAttribute('data-bump-id') || 'unknown';
      const bumpPrice = parseFloat(bump.getAttribute('data-bump-price') || '0');
      
      const handleBumpChange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        trackBumpInteraction(
          bumpId,
          target.checked ? 'add' : 'remove',
          bumpPrice,
          offerId
        );
      };
      
      bump.addEventListener('change', handleBumpChange);
    });
    
  }, [checkoutRef, offerId]);

  // Estas funções estão aqui como referência para implementações futuras
  // Se você precisar interceptar os callbacks do componente original
  // Atualmente não usados para evitar warnings, mas mantidos para documentação
  /*
  const wrappedOnSubmit = (e: any) => {
    // Extrair dados relevantes do formulário
    if (checkoutRef?.current) {
      const form = checkoutRef.current.querySelector('form');
      if (form) {
        const formData = new FormData(form as HTMLFormElement);
        const paymentMethod = formData.get('paymentMethod')?.toString() || 'card';
        const installments = parseInt(formData.get('installments')?.toString() || '1');
        
        // Tentar encontrar o valor total
        const totalAmountElement = checkoutRef.current.querySelector('[data-total-amount]');
        let amount = 0;
        
        if (totalAmountElement && totalAmountElement.getAttribute('data-total-amount')) {
          amount = parseFloat(totalAmountElement.getAttribute('data-total-amount') || '0');
        }
        
        // Verificar se há bumps selecionados
        const selectedBumps: string[] = [];
        const bumpCheckboxes = checkoutRef.current.querySelectorAll('[data-bump-id]:checked');
        bumpCheckboxes.forEach(bump => {
          const bumpId = bump.getAttribute('data-bump-id');
          if (bumpId) selectedBumps.push(bumpId);
        });
        
        // Verificar se há cupom aplicado
        const hasCoupon = !!checkoutRef.current.querySelector('[data-coupon-applied="true"]');
        let couponDiscount = 0;
        const couponElement = checkoutRef.current.querySelector('[data-coupon-discount]');
        if (couponElement) {
          couponDiscount = parseFloat(couponElement.getAttribute('data-coupon-discount') || '0');
        }
        
        // Rastrear início do pagamento
        trackPaymentInitiated({
          paymentMethod,
          amount,
          installments,
          hasBumps: selectedBumps.length > 0,
          bumpQuantity: selectedBumps.length,
          selectedBumps,
          couponApplied: hasCoupon,
          couponDiscount,
          offerId
        });
      }
    }
    
    // Chamar o callback original
    if (onSubmitCallback) {
      onSubmitCallback(e);
    }
  };

  const wrappedOnSuccess = (data: any) => {
    // Extrair dados para analytics
    const paymentMethod = data.data?.paymentMethod || 'card';
    const amount = data.data?.amount || 0;
    const orderId = data.data?.orderId || data.data?.id || 'unknown';
    const installments = data.data?.installments || 1;
    
    if (paymentMethod === 'pix') {
      // Rastrear redirecionamento para PIX
      trackPixRedirect({
        paymentMethod,
        amount,
        installments,
        offerId
      }, orderId);
    } else {
      // Rastrear sucesso no pagamento
      trackPaymentSuccess({
        paymentMethod,
        amount,
        installments,
        offerId
      }, orderId);
    }
    
    // Chamar o callback original
    if (onSuccessCallback) {
      onSuccessCallback(data);
    }
  };

  const wrappedOnError = (error: any) => {
    // Rastrear erro
    let errorMessage = error?.message || 'Unknown error';
    let errorType: 'payment' | 'validation' | 'server' | 'processing' = 'processing';
    
    if (errorMessage.includes('declined') || errorMessage.includes('recusado')) {
      errorType = 'payment';
    } else if (errorMessage.includes('valid') || errorMessage.includes('required')) {
      errorType = 'validation';
    } else if (errorMessage.includes('server') || errorMessage.includes('API')) {
      errorType = 'server';
    }
    
    trackCheckoutError({
      errorType,
      errorMessage,
      paymentMethod: 'unknown' // Não temos como saber neste ponto
    }, offerId);
    
    // Chamar o callback original
    if (onErrorCallback) {
      onErrorCallback(error);
    }
  };
  */

  // Este componente não renderiza nada
  return null;
};

export default CheckoutAnalytics;
